import Quill from "quill";
import ReactDOM from "react-dom";
import QuillTable from "./QuillTable.jsx";

const BlockEmbed = Quill.import("blots/block/embed");

export class TableBlot extends BlockEmbed {
  static blotName = "customTable";
  static tagName = "div";
  static className = "custom-table-container";

  static create(value) {
    const node = super.create();
    node.setAttribute("contenteditable", "false");
    node.setAttribute("data-custom-table", "true");
    // Allow context menu to work on table
    node.style.pointerEvents = "auto";
    const id =
      value?.id ||
      `custom-table-${Date.now()}-${crypto.getRandomValues(new Uint32Array(1))[0]}`;
    node.setAttribute("data-id", id);

    const reactRoot = document.createElement("div");
    reactRoot.setAttribute("data-react-root", "true");
    let columns = value?.columns || [];
    let data = value?.data || [];

    if (!columns.length || !data.length) {
      const tempTable = document.createElement("div");
      tempTable.innerHTML = value?.html || "";
      const table = tempTable.querySelector("table");

      if (table) {
        const ths = table.querySelectorAll("thead th");
        columns = Array.from(ths).map((th, index) => {
          // Extract HTML content from header, looking for p tag with class
          const className = `quillHeading${index}`;
          const pTag = th.querySelector(`p.${className}`);
          const title = pTag?.innerHTML.trim() || th.textContent?.trim() || "Column";
          return {
            title: title,
            dataIndex: `col${index}`,
            key: `col${index}`,
          };
        });

        const rows = [];
        const trs = table.querySelectorAll("tbody tr");
        trs.forEach((tr, rowIndex) => {
          const rowData = { key: `${rowIndex + 1}` };
          const tds = tr.querySelectorAll("td");
          tds.forEach((td, cellIndex) => {
            const col = columns[cellIndex];
            if (col) {
              // Extract HTML content from cell
              // First, try to find all p tags with the expected class
              const className = `quillInput${rowIndex}${cellIndex}`;
              const pTags = td.querySelectorAll(`p.${className}`);
              
              let cellContent = "";
              if (pTags.length > 0) {
                // If we found p tags with the class, get their combined HTML
                cellContent = Array.from(pTags)
                  .map(p => p.outerHTML)
                  .join("");
              } else {
                // Fallback: get all innerHTML of the td, but wrap in p tags if needed
                const innerHTML = td.innerHTML.trim();
                if (innerHTML) {
                  // Check if it already has p tags
                  const tempDiv = document.createElement("div");
                  tempDiv.innerHTML = innerHTML;
                  const hasPTags = tempDiv.querySelector("p");
                  if (hasPTags) {
                    cellContent = innerHTML;
                  } else {
                    // Wrap in p tag with class
                    cellContent = `<p class="${className}">${innerHTML}</p>`;
                  }
                }
              }
              
              rowData[col.dataIndex] = cellContent;
            }
          });
          rows.push(rowData);
        });

        data = rows;
      }
    }

    node.appendChild(reactRoot);

    ReactDOM.render(
      <QuillTable initialColumns={columns} initialData={data} location={window.location} />,
      reactRoot
    );

    return node;
  }

  static value(node) {
    const reactRoot = node.querySelector("[data-react-root]");
    if (!reactRoot) return { type: "customTable" };

    const table = reactRoot.querySelector("table");
    if (!table) return { type: "customTable" };

    const ths = table.querySelectorAll("thead th");

    const headers = Array.from(ths).map((th, index) => {
      const className = `quillHeading${index}`;
      const pTag = th.querySelector(`p.${className}`);
      return {
        title: pTag?.innerHTML.trim() || "Column",
        dataIndex: `col${index}`,
        key: `col${index}`,
      };
    });

    const rows = [];
    const bodyRows = table.querySelectorAll("tbody tr");

    bodyRows.forEach((row, rowIndex) => {
      const rowData = {
        key: `${rowIndex + 1}`,
      };
      const cells = row.querySelectorAll("td");
      cells.forEach((cell, cellIndex) => {
        const header = headers[cellIndex];
        if (header) {
          const className = `quillInput${rowIndex}${cellIndex}`;
          // Get all p tags with the class, not just the first one
          const pTags = cell.querySelectorAll(`p.${className}`);
          if (pTags.length > 0) {
            // Combine all p tags' HTML
            rowData[header.dataIndex] = Array.from(pTags)
              .map(p => p.outerHTML)
              .join("");
          } else {
            // Fallback: get innerHTML of the cell's content div
            const contentDiv = cell.querySelector("div[style*='padding']");
            rowData[header.dataIndex] = contentDiv?.innerHTML.trim() || cell.innerHTML.trim() || "";
          }
        }
      });
      rows.push(rowData);
    });

    try {
      return {
        type: "customTable",
        columns: headers,
        data: rows,
      };
    } catch {
      return { type: "customTable" };
    }
  }
}

Quill.register(TableBlot);
